import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Alert from '../components/Alert.jsx';
import Loader from '../components/Loader.jsx';
import MatchControl from '../components/MatchControl.jsx';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';

// The bracket page (/:slug/bracket): every set of an event by phase, pool
// and round, with its state, score and where it is (stream, station), and
// for accounts with start_match_enabled and a saved start.gg token the
// actions on each: Show on overlay (the In-Match card's pick), Put on my
// stream (start.gg's stream queue) and Open, the match control (Call,
// Start, games, winner, DQ, reset). Read in full on open, then every 15 s
// only the sets changed since (updatedAfter), with the user's own token when
// saved, so a big bracket never eats the overlays' start.gg budget.
const REFRESH_MS = 15_000;
// A set changed just before a read finished would slip past a strict
// "since the read": look back a little; the rows merge by id anyway.
const REFRESH_LOOKBACK_S = 30;
const STATE_WORD = { 1: 'Not started', 2: 'In progress', 3: 'Completed', 4: 'Ready', 6: 'Called', 7: 'Queued' };

// Winners rounds first, in order; then losers rounds (negative on
// start.gg), in order; then by the set's letter within the pool.
const roundKey = (r) => (r.round > 0 ? r.round : 1000 - r.round);
const byRound = (a, b) => roundKey(a) - roundKey(b) || String(a.identifier ?? '').localeCompare(String(b.identifier ?? ''), undefined, { numeric: true });

export default function Bracket() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [token, setToken] = useState(null);
  const [rows, setRows] = useState({});
  const [eventName, setEventName] = useState('');
  const [total, setTotal] = useState(0);
  const [complete, setComplete] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [pick, setPick] = useState('');
  const [rowBusy, setRowBusy] = useState(null);
  const [rowMessage, setRowMessage] = useState(null);
  const loadedAtRef = useRef(null);

  const eventId = params.get('eventId') || '';
  const canAct = user?.startMatchEnabled === true && !!token?.saved;

  useEffect(() => {
    let cancelled = false;
    api.packageBracket(slug)
      .then((m) => {
        if (cancelled) return;
        setMeta(m);
        setPick(m.pick ?? '');
        if (!eventId && m.events?.length) {
          const open = m.events.find((e) => e.state !== 'COMPLETED') ?? m.events[0];
          setParams({ eventId: open.id }, { replace: true });
        }
      })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    if (user?.startMatchEnabled) {
      api.startggTokenStatus()
        .then((t) => { if (!cancelled) setToken(t); })
        .catch(() => { if (!cancelled) setToken({ saved: false }); });
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    setRows({});
    setSelected(null);
    setLoading(true);
    setError(null);
    loadedAtRef.current = null;
    const read = async () => {
      if (document.visibilityState === 'hidden' && loadedAtRef.current) return;
      const since = loadedAtRef.current ? Math.max(0, loadedAtRef.current - REFRESH_LOOKBACK_S) : null;
      try {
        const r = await api.packageBracket(slug, { eventId, updatedAfter: since });
        if (cancelled) return;
        setEventName(r.event?.name ?? '');
        if (since == null) {
          setTotal(r.total);
          setComplete(r.complete);
          setRows(Object.fromEntries(r.sets.map((s) => [s.id, s])));
        } else if (r.sets.length) {
          setRows((prev) => {
            const next = { ...prev };
            for (const s of r.sets) next[s.id] = s;
            return next;
          });
        }
        loadedAtRef.current = r.loadedAt;
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    read();
    const timer = setInterval(read, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [slug, eventId]);

  // Phases in order, each its pools in order, each its sets by round.
  const groups = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const list = Object.values(rows).filter((r) => !needle
      || r.players.some((p) => (p.name ?? '').toLowerCase().includes(needle))
      || (r.roundText ?? '').toLowerCase().includes(needle)
      || String(r.identifier ?? '').toLowerCase() === needle);
    const phases = new Map();
    for (const r of list) {
      const pk = r.phaseId ?? '';
      if (!phases.has(pk)) phases.set(pk, { id: pk, name: r.phaseName ?? 'Bracket', order: r.phaseOrder ?? 0, pools: new Map() });
      const phase = phases.get(pk);
      const gk = r.poolId ?? '';
      if (!phase.pools.has(gk)) phase.pools.set(gk, { id: gk, label: r.pool, sets: [] });
      phase.pools.get(gk).sets.push(r);
    }
    return [...phases.values()]
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
      .map((phase) => ({
        ...phase,
        pools: [...phase.pools.values()]
          .sort((a, b) => String(a.label ?? '').localeCompare(String(b.label ?? ''), undefined, { numeric: true }))
          .map((pool) => ({ ...pool, sets: pool.sets.sort(byRound) })),
      }));
  }, [rows, filter]);

  // The match control's answer, folded into the row it came from.
  const mergeMatch = (m) => {
    setRows((prev) => {
      const row = prev[m.setId];
      if (!row) return prev;
      return {
        ...prev,
        [m.setId]: {
          ...row,
          state: m.state,
          completed: m.completed,
          players: row.players.map((p, i) => ({
            ...p,
            score: m.players[i]?.score ?? p.score,
            winner: m.winner === i + 1,
          })),
        },
      };
    });
  };

  const rowAction = async (row, kind) => {
    setRowBusy(row.id + kind);
    setRowMessage(null);
    try {
      if (kind === 'overlay') {
        const r = await api.packageSetOnStream(slug, pick === row.id ? '' : row.id);
        setPick(r.onStream?.setId ?? '');
      } else {
        const r = await api.packageMatchControl(slug, { setId: row.id, action: 'stream' });
        if (r?.match) mergeMatch(r.match);
        setRows((prev) => ({ ...prev, [row.id]: { ...prev[row.id], streamName: meta?.stream?.streamName ?? meta?.stream?.name ?? prev[row.id].streamName } }));
      }
    } catch (e) {
      setRowMessage(`${row.players.map((p) => p.name ?? 'TBD').join(' vs ')}: ${e.message}`);
    } finally {
      setRowBusy(null);
    }
  };

  // A set's stream is start.gg's name for it (a YouTube channel's title),
  // which the console's Stream field needn't be: the endpoint gives both.
  const myStreamName = meta?.stream?.streamName ?? meta?.stream?.name ?? '';
  const onMyStream = (row) => !!myStreamName && (row.streamName ?? '').toLowerCase() === myStreamName.toLowerCase();
  const selectedRow = selected ? rows[selected] : null;

  if (error && !meta) {
    return (
      <>
        <Alert kind="danger">{error}</Alert>
        <Link to={`/${slug}`} className="btn btn-outline-secondary btn-sm">← Back to the console</Link>
      </>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <Link to={`/${slug}`} className="btn btn-outline-secondary btn-sm">← Console</Link>
          <h4 className="mb-0">
            Bracket
            {meta?.tournament?.name && <span className="text-muted fw-normal"> — {meta.tournament.name}</span>}
          </h4>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {meta?.events?.length > 1 && (
            <select
              className="form-select form-select-sm"
              aria-label="Event"
              value={eventId}
              onChange={(e) => setParams({ eventId: e.target.value })}
            >
              {meta.events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          )}
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder="Find a player, round or set…"
            aria-label="Find a set"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {meta && (
        <p className="text-muted small">
          {meta.stream?.name ? (
            meta.stream.id
              ? <>Your stream: <strong>{meta.stream.name}</strong>{meta.stream.streamName && meta.stream.streamName.toLowerCase() !== meta.stream.name.toLowerCase() ? <> ({meta.stream.streamName} on start.gg)</> : null}.</>
              : <>Your stream <strong>{meta.stream.name}</strong> isn&apos;t registered on this tournament&apos;s start.gg page, so sets can&apos;t be put on it from here.</>
          ) : 'No stream on your start.gg link.'}
          {' '}
          {user?.startMatchEnabled !== true
            ? 'Running matches from the console isn’t enabled for your account, so this page is read-only.'
            : token && !token.saved
              ? 'No start.gg token for this account yet: the admin adds one, and then the sets can be acted on.'
              : ''}
          {!complete && ` Showing the first ${Object.keys(rows).length} of ${total} sets.`}
        </p>
      )}
      {error && meta && <Alert kind="warning">{error}</Alert>}
      {rowMessage && <Alert kind="danger">{rowMessage}</Alert>}

      {selectedRow && (
        <div className="card card-fui mb-3">
          <div className="card-header d-flex align-items-center justify-content-between gap-2">
            <h6 className="mb-0">
              {selectedRow.roundText}
              {selectedRow.pool ? <span className="text-muted fw-normal"> · Pool {selectedRow.pool}</span> : null}
              <span className="text-muted fw-normal"> · set {selectedRow.identifier}</span>
            </h6>
            <div className="d-flex gap-2">
              {canAct && !onMyStream(selectedRow) && meta?.stream?.id && (
                <button type="button" className="btn-ghost btn-ghost--primary" disabled={!!rowBusy} onClick={() => rowAction(selectedRow, 'stream')}>
                  Put on my stream
                </button>
              )}
              <button type="button" className="btn-ghost btn-ghost--primary" disabled={!!rowBusy} onClick={() => rowAction(selectedRow, 'overlay')}>
                {pick === selectedRow.id ? 'Overlay back to Auto' : 'Show on overlay'}
              </button>
              <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
          <div className="card-body">
            <MatchControl packageSlug={slug} setId={selectedRow.id} enabled={canAct} onMatch={mergeMatch} />
          </div>
        </div>
      )}

      {loading && <Loader label="Loading the bracket…" />}

      {!loading && groups.length === 0 && (
        <div className="card card-fui"><div className="card-body text-center text-muted">No sets {filter ? 'match that' : 'in this event yet'}.</div></div>
      )}

      {groups.map((phase) => phase.pools.map((pool) => (
        <div className="card card-fui mb-3" key={`${phase.id}|${pool.id}`}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              {phase.name}
              {pool.label && phase.pools.length > 1 ? <span className="text-muted fw-normal"> · Pool {pool.label}</span> : null}
            </h6>
            <span className="text-muted small">{pool.sets.length} {pool.sets.length === 1 ? 'set' : 'sets'}</span>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-neon table-hover mb-0">
                <thead>
                  <tr>
                    <th>Set</th>
                    <th>Round</th>
                    <th>Players</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th>Where</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pool.sets.map((r) => {
                    const [a, b] = r.players;
                    const name = (p) => (p?.name
                      ? <span className={p.winner ? 'winner' : ''}>{p.name}{p.winner && ' ★'}</span>
                      : <span className="text-muted">TBD</span>);
                    const scored = r.state === 2 || r.completed || r.players.some((p) => p.score > 0);
                    return (
                      <tr key={r.id} className={selected === r.id ? 'table-active' : ''}>
                        <td><code>{r.identifier ?? '—'}</code></td>
                        <td>{r.roundText ?? (r.round != null ? `Round ${r.round}` : '—')}</td>
                        <td>{name(a)} <span className="text-muted">vs</span> {name(b)}</td>
                        <td>
                          {r.displayScore === 'DQ'
                            ? <span className="text-warning">DQ</span>
                            : scored ? <code>{a?.score ?? 0}–{b?.score ?? 0}</code> : <span className="text-muted">—</span>}
                        </td>
                        <td>
                          <span className={`badge ${r.completed ? 'badge-completed' : 'badge-pending'}`}>
                            {r.completed ? 'Completed' : STATE_WORD[r.state] ?? 'Pending'}
                          </span>
                        </td>
                        <td className="small">
                          {r.streamName ? `on ${r.streamName}` : r.station != null ? `Station ${r.station}` : <span className="text-muted">—</span>}
                          {pick === r.id && <span className="text-info"> · overlay</span>}
                        </td>
                        <td className="text-end text-nowrap">
                          {!r.completed && (
                            <button type="button" className="btn btn-link btn-sm py-0" disabled={!!rowBusy} onClick={() => rowAction(r, 'overlay')}>
                              {pick === r.id ? 'Auto' : 'Overlay'}
                            </button>
                          )}
                          {canAct && !r.completed && !onMyStream(r) && meta?.stream?.id && (
                            <button type="button" className="btn btn-link btn-sm py-0" disabled={!!rowBusy} onClick={() => rowAction(r, 'stream')}>
                              Stream
                            </button>
                          )}
                          <button type="button" className="btn btn-link btn-sm py-0" onClick={() => setSelected(selected === r.id ? null : r.id)}>
                            {selected === r.id ? 'Close' : 'Open'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )))}
    </>
  );
}
